import { PageHeader } from '@organisms';
import { Button, message, Space } from 'antd';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { ValidationError } from 'yup';

import { showConfirmModal } from 'src/features/programs/utils/modalHelpers';

import { useProgramStepContext } from '../contexts/ProgramStepContext';
import { useCreateOrUpdateProgram } from '../hooks/useCreateOrUpdateProgram';
import { contentStepSchema } from '../schemas/contentStepSchema';
import { visualStepSchema } from '../schemas/visualStepSchema';
import { showErrorModal, showInfoModal } from '../utils/modalHelpers';
import { ProgramStepper, ProgramStepperProps } from './ProgramStepper';

interface ProgramStepHeaderProps extends ProgramStepperProps {
  disabled?: boolean;
}

export const ProgramStepHeader: FC<ProgramStepHeaderProps> = ({
  programId,
  currentStep,
  onChangeStep,
}) => {
  const {
    stepValuesRef,
    validateForms,
    program,
    getPublishedUpdatePayload,
    getStepDefaultValues,
    isLoadingProgram,
    isPublishedProgram,
    programApiInitialValues,
  } = useProgramStepContext();

  const navigate = useNavigate();

  const { updatePublishedProgram, isLoading } =
    useCreateOrUpdateProgram(programId);

  function hasImageError(errors: ValidationError[]): boolean {
    return errors.some(error => error.path?.includes('.preview'));
  }

  function showImageErrorModal(step: number) {
    showErrorModal({
      title: 'Imagens obrigatórias incompletas',
      content: `As imagens obrigatórias do passo ${
        step + 1
      } não foram preenchidas, por favor preencha e tente publicar novamente.`,
    });
  }

  function showStepInfoModal(step: number) {
    showInfoModal({
      title: 'Ops! Não foi possível salvar as alterações',
      content: `Por favor, revise as informações do passo ${
        step + 1
      } e tente novamente.`,
    });
  }

  const handleUpdatePublishedProgram = async () => {
    const configurationStepValues = getStepDefaultValues('configurationStep');

    const contentStepValues =
      getStepDefaultValues('contentStep') ??
      programApiInitialValues?.contentStep;

    const visualStepValues =
      getStepDefaultValues('visualStep') ?? programApiInitialValues?.visualStep;

    const selectedGenders = configurationStepValues?.gender;

    const hasUpdatedGenders =
      configurationStepValues?.gender.toString() !==
      programApiInitialValues?.configurationStep.gender.toString();

    if (selectedGenders) {
      try {
        await contentStepSchema.validate(contentStepValues, {
          abortEarly: false,
          context: { gender: selectedGenders },
        });
      } catch (error) {
        if (error instanceof ValidationError) {
          const isImageError = hasImageError(error.inner);
          onChangeStep(2);
          return isImageError ? showImageErrorModal(2) : showStepInfoModal(2);
        }
      }

      try {
        await visualStepSchema.validate(visualStepValues, {
          abortEarly: false,
          context: { gender: selectedGenders },
        });
      } catch (error) {
        if (error instanceof ValidationError) {
          const isImageError = hasImageError(error.inner);
          onChangeStep(3);
          return isImageError ? showImageErrorModal(3) : showStepInfoModal(3);
        }
      }
    }

    if (stepValuesRef.current && !hasUpdatedGenders) {
      const invalidStep = validateForms();

      if (invalidStep !== undefined && ![2, 3].includes(invalidStep)) {
        onChangeStep(invalidStep);

        return showStepInfoModal(invalidStep);
      }
    }

    const payload = getPublishedUpdatePayload();

    if (!payload) {
      return;
    }

    return showConfirmModal({
      title: 'Confirmar Publicação',
      content:
        'Tem certeza de que deseja publicar as alterações? Esta ação não pode ser desfeita.',
      onOk: async close => {
        try {
          await updatePublishedProgram(payload);
          message.success({
            content: 'As alterações foram publicadas com sucesso!',
          });
          close();
        } catch (error) {
          message.error({
            content:
              'Não foi possível atualizar os dados do programa publicado, tente novamente.',
          });
          close();
        }
      },
    });
  };

  const confirmBackToPath = () => {
    showConfirmModal({
      title: 'Sair do programa',
      content:
        'Tem certeza de que deseja sair do programa? Você perderá todas as alterações não salvas.',
      onOk: () => {
        navigate('/programs/v2');
      },
    });
  };

  return (
    <Space direction='vertical' size={'large'} style={{ width: '100%' }}>
      <PageHeader
        backToPath={programId ? confirmBackToPath : '/programs/v2'}
        title={
          programId && program
            ? program.name
            : isLoadingProgram
              ? ''
              : 'Criar Programa'
        }
        subTitle=''
        hasDivider={false}
        extra={
          isPublishedProgram && (
            <Space style={{ marginLeft: 'auto' }}>
              <Button
                type='primary'
                htmlType='button'
                loading={isLoadingProgram || isLoading}
                onClick={handleUpdatePublishedProgram}
                style={{
                  borderRadius: '6px',
                  paddingLeft: '16px',
                  paddingRight: '16px',
                }}
              >
                Publicar Alterações
              </Button>
            </Space>
          )
        }
      />

      <ProgramStepper
        programId={programId}
        currentStep={currentStep}
        onChangeStep={onChangeStep}
      />
    </Space>
  );
};
